"use client";

import { PRESENT_RISK_ACCEPTED_TOOL_MESSAGE } from "@/src/components/ChatOnboarding/presentRiskAndGoalsFeedback";
import { persistSaveGoalDraftId } from "./saveGoalDraftIdStorage";
import {
  planQueryKeys,
  useChatOnboardingApi,
  useChatRecommendQuestion,
  useIsPc,
  useOpenChatModal,
  useShellRouter,
} from "@/src/hooks";
import { useCallback } from "react";

import { ChatStore } from "@/src/store";
import type { ParsedPresentRiskAndGoals } from "@/src/components/ChatOnboarding/parsePresentRiskAndGoalsArgs";
import log from "logbeacon";
import { navigateExitOnboardingToMainChat } from "@/src/utils/chatUtils/exitOnboardingToMainChat";
import { trackEvent } from "@/src/utils/tracking";
import { useCompleteOnboarding } from "./useCompleteOnboarding";
import { usePathname } from "@/src/hooks/usePathname";
import { useQueryClient } from "@tanstack/react-query";

export type UsePresentRiskAndGoalsFlowParams = {
  presentation?: ParsedPresentRiskAndGoals | null;
  isLast?: boolean;
  hasResult?: boolean;
  isActionDisabled: boolean;
  enabled?: boolean;
  submitInteractiveToolMessage: (
    message: string,
    options?: { interactiveToolOnly?: boolean },
  ) => void;
};

/**
 * present_risk_and_goals inline card — CTA submits accept feedback;
 * onboarding exit side-effects on build-plan click.
 */
export const usePresentRiskAndGoalsFlow = ({
  presentation,
  enabled = false,
  isActionDisabled,
  submitInteractiveToolMessage,
  hasResult,
}: UsePresentRiskAndGoalsFlowParams) => {
  const onboardingApi = useChatOnboardingApi();
  const [isPC] = useIsPc();
  const pathname = usePathname();
  const router = useShellRouter();
  const handleOpenChatModal = useOpenChatModal({ skipShellNavigation: true });
  const { questions } = useChatRecommendQuestion();
  const queryClient = useQueryClient();
  const { mutateAsync: completeOnboarding, isPending: isCompletingOnboarding } =
    useCompleteOnboarding();

  const handleBuildPlanClick = useCallback(async () => {
    if (
      isActionDisabled ||
      !presentation ||
      hasResult ||
      isCompletingOnboarding
    ) {
      return;
    }

    const goalId = presentation.payload.goalDraft?.goalId;
    const goalTitle = presentation.payload.goalDraft?.title;

    if (goalId) {
      persistSaveGoalDraftId(goalId);
    }

    if (enabled) {
      trackEvent("ob_funnel_report_view");
    }

    if (enabled && !hasResult) {
      const { shouldEnter } = await onboardingApi.getOnboardingStatus();
      if (shouldEnter) {
        await completeOnboarding();
        log.info(
          "[present_risk_and_goals] build plan click — complete onboarding",
          {
            goalId,
            goalTitle,
          },
        );
        setTimeout(async () => {
          navigateExitOnboardingToMainChat({
            isPC,
            router,
            openChatModal: handleOpenChatModal,
            questions,
            pathname,
            isChatModalOpen: Boolean(ChatStore.hasChatModal),
          });
          queryClient.removeQueries({
            queryKey: planQueryKeys.diagnosisGoals(),
          });
        }, 1000);
      }
    }

    log.info(
      "[present_risk_and_goals] build plan click — submit accept feedback",
      {
        goalId,
        goalTitle,
      },
    );
    submitInteractiveToolMessage(PRESENT_RISK_ACCEPTED_TOOL_MESSAGE, {
      interactiveToolOnly: true,
    });
  }, [
    completeOnboarding,
    enabled,
    handleOpenChatModal,
    hasResult,
    isActionDisabled,
    isCompletingOnboarding,
    isPC,
    onboardingApi,
    pathname,
    presentation,
    queryClient,
    questions,
    router,
    submitInteractiveToolMessage,
  ]);

  return {
    handleBuildPlanClick,
    isCompletingOnboarding,
  };
};
